import React, { useState } from 'react';
import { ChevronRight, ChevronDown } from 'lucide-react';

import {
  ScopeResponseRoom,
  ScopeResponseLineItem,
  NoMatchItem,
} from '../../types';
import { ScopeLineItem } from './ScopeLineItem';
import { ScopeDebugDrawer } from './ScopeDebugDrawer';

/**
 * Renders one no-match item with an optional debug-trace chevron.
 */
const NoMatchRow: React.FC<{
  item: NoMatchItem;
  showDebug: boolean;
  stepMode: boolean;
}> = ({ item, showDebug, stepMode }) => {
  const [open, setOpen] = useState(false);
  const hasDebug = showDebug && Boolean(item.debug);

  return (
    <li>
      <div className="flex items-start gap-2">
        {hasDebug && (
          <button
            onClick={() => setOpen((v) => !v)}
            className="mt-0.5 shrink-0 rounded p-0.5 text-purple-700 hover:bg-purple-100"
            aria-label={open ? 'Hide pipeline trace' : 'Show pipeline trace'}
          >
            {open ? (
              <ChevronDown className="h-3.5 w-3.5" />
            ) : (
              <ChevronRight className="h-3.5 w-3.5" />
            )}
          </button>
        )}
        <div className="flex-1">
          <span className="font-medium">{item.activity_description}</span>
          <span className="block text-red-700">{item.reason}</span>
          {hasDebug && open && item.debug && (
            <ScopeDebugDrawer debug={item.debug} stepMode={stepMode} />
          )}
        </div>
      </div>
    </li>
  );
};

// ================================================================= //
//          Heading Order & Item Categorization                        //
// ================================================================= //

/**
 * Defines the top-to-bottom ordering of items within a room.
 * Items are sorted Ceiling -> Walls -> Trim -> Doors -> Windows -> Floor
 * then Cabinets -> Fixtures -> Appliances -> Contents -> General.
 *
 * SECTION_LABEL holds the display-cased heading the UI renders above each
 * group; SECTION_ORDER controls the sort + render order.
 */
export const SECTION_ORDER: Record<string, number> = {
  misc: 0,
  ceiling: 1,
  walls: 2,
  trim: 3,
  doors: 4,
  windows: 5,
  floor: 6,
  // Backend room-type composite groups (app.services.scope_org.taxonomy).
  cabinets_appliances_plumbing: 7,
  vanities_fixtures_plumbing: 7,
  cabinets: 7,
  fixtures: 8,
  appliances: 9,
  contents: 10,
  general: 11,
};

export const SECTION_LABEL: Record<string, string> = {
  misc: 'Misc',
  ceiling: 'Ceiling',
  walls: 'Walls',
  trim: 'Trim',
  doors: 'Doors',
  windows: 'Windows',
  floor: 'Floor',
  cabinets_appliances_plumbing: 'Cabinets, Appliances, and Plumbing',
  vanities_fixtures_plumbing: 'Vanities, Fixtures, and Plumbing',
  cabinets: 'Cabinets',
  fixtures: 'Fixtures',
  appliances: 'Appliances',
  contents: 'Contents',
  general: 'General',
};

// Reason: kept for backwards-compat with any imports of the old name.
const HEADING_ORDER = SECTION_ORDER;

/**
 * Real Tampa-Feb-2026 Xactimate category prefixes -> section.
 * Categories where the section depends on surface (DRY, PNT, INS, WTR
 * material-specific codes) get a fallback section here; for fine-grained
 * placement the frontend defers to keyword matching against the
 * description string.
 */
const CODE_PREFIX_MAP: Record<string, string> = {
  // Ceiling-specific
  ACT: 'ceiling',           // Acoustic ceiling tile
  // Walls / interior surfaces (default — refined by keyword if 'ceiling' appears)
  DRY: 'walls',             // Drywall
  PNT: 'walls',             // Paint
  INS: 'walls',             // Insulation
  // Water restoration material-specific (default ceiling — most leaks are top-down)
  WTR: 'ceiling',
  // Trim family
  FNC: 'trim',              // Finish carpentry — baseboard, crown, casing, shoe
  // Doors / windows
  DOR: 'doors',
  WDA: 'windows',
  WDV: 'windows',
  WDP: 'windows',
  WDR: 'windows',           // Window reglaze
  GLS: 'windows',           // Glass
  // Floor coverings
  FCC: 'floor',             // Carpet
  FCV: 'floor',             // Vinyl
  FCW: 'floor',             // Wood / laminate
  FCT: 'floor',             // Tile
  FCS: 'floor',             // Sheet vinyl
  // Cabinetry
  CAB: 'cabinets',
  // Fixtures
  FNH: 'fixtures',          // Bath fixtures, door hardware
  PLM: 'fixtures',          // Plumbing
  LIT: 'fixtures',          // Lighting
  ELS: 'fixtures',          // Electrical fixtures (some entries)
  // Appliances
  APP: 'appliances',
  // General / restoration-wide
  DMO: 'general',           // Demo
  CLN: 'general',           // Cleaning
  HVC: 'general',           // HVAC
  RFG: 'general',           // Roofing
  SFG: 'general',           // Siding / soffit / gutter
};

/**
 * Description keyword to heading category mapping (case-insensitive).
 */
const KEYWORD_MAP: Array<[string, string]> = [
  ['ceiling', 'ceiling'],
  ['drywall ceiling', 'ceiling'],
  ['popcorn', 'ceiling'],
  ['wall', 'walls'],
  ['drywall', 'walls'],
  ['paint', 'walls'],
  ['plaster', 'walls'],
  ['texture', 'walls'],
  ['prime', 'walls'],
  ['trim', 'trim'],
  ['baseboard', 'trim'],
  ['base board', 'trim'],
  ['crown', 'trim'],
  ['casing', 'trim'],
  ['molding', 'trim'],
  ['moulding', 'trim'],
  ['door', 'doors_windows'],
  ['window', 'doors_windows'],
  ['floor', 'floor'],
  ['carpet', 'floor'],
  ['vinyl', 'floor'],
  ['tile', 'floor'],
  ['laminate', 'floor'],
  ['hardwood', 'floor'],
  ['pad', 'floor'],
  ['underlayment', 'floor'],
  ['cabinet', 'cabinets'],
  ['vanity', 'cabinets'],
  ['counter', 'cabinets'],
  ['countertop', 'cabinets'],
  ['fixture', 'fixtures'],
  ['faucet', 'fixtures'],
  ['toilet', 'fixtures'],
  ['sink', 'fixtures'],
  ['light', 'fixtures'],
  ['fan', 'fixtures'],
  ['appliance', 'appliances'],
  ['refrigerator', 'appliances'],
  ['dishwasher', 'appliances'],
  ['range', 'appliances'],
  ['oven', 'appliances'],
  ['microwave', 'appliances'],
  ['content', 'contents'],
];

/**
 * Returns the section name (one of SECTION_ORDER keys) for a line item.
 * Uses description keywords first when they would override the cat-default
 * (e.g., a DRY code for ceiling drywall: cat-default is 'walls', but the
 * description 'ceiling' bumps it to 'ceiling'). Falls back to cat prefix,
 * then keyword scan, then 'general'.
 */
export function sectionForItem(item: ScopeResponseLineItem): string {
  // Backend-authoritative: the scope organizer stamps the canonical group key.
  // Fall back to the keyword heuristic only for legacy scopes without it.
  if (item.surface) return item.surface;

  const code = (item.xactimate_code || '').toUpperCase();
  const desc = (item.description || '').toLowerCase();

  // Reason: Texture and paint always belong with the surface they finish —
  // bucket them by ceiling-vs-walls cue in the description rather than the
  // cat default for PNT/DRY TEX. If no surface cue, default walls (paint
  // typically goes on walls in residential).
  const codePrefix = code.split(' ')[0];
  if (codePrefix === 'PNT' || code.startsWith('DRY TEX')) {
    if (desc.includes('ceiling') || desc.includes('popcorn')) return 'ceiling';
    return 'walls';
  }

  // Reason: DRY and WTR DRYW* depend on surface — peek at description for
  // 'ceiling' before defaulting to the cat-default ('walls' for DRY,
  // 'ceiling' for WTR).
  if (codePrefix === 'DRY' && desc.includes('ceiling')) return 'ceiling';
  if (codePrefix === 'WTR' && desc.includes('wall') && !desc.includes('ceiling'))
    return 'walls';

  // Cat-prefix lookup
  for (const [prefix, section] of Object.entries(CODE_PREFIX_MAP)) {
    if (codePrefix === prefix) return section;
  }

  // Description-keyword fallback
  for (const [keyword, section] of KEYWORD_MAP) {
    if (desc.includes(keyword)) return section;
  }

  return 'general';
}

/**
 * Kept for backwards compatibility with callers that just want a sort key.
 * Prefer sectionForItem() + groupItemsBySection() for new code.
 */
export function categorizeItem(item: ScopeResponseLineItem): number {
  return SECTION_ORDER[sectionForItem(item)] ?? SECTION_ORDER.general;
}

/**
 * Workflow ordering within a section: removals first, then cleaning /
 * treating (M/I), then replacements, then texture, then paint. Mirrors
 * how a working estimator orders trades on a job.
 */
const ACTIVITY_PRIORITY: Record<string, number> = {
  '-': 1,        // Remove
  'D/R': 2,      // Detach & Reset
  'M': 3,        // Material only (prep)
  'I': 4,        // Install only
  '&': 5,        // Remove & Replace
  '+': 6,        // Replace
};

export function workflowOrderForItem(item: ScopeResponseLineItem): number {
  // Backend-authoritative within-room ordinal when present.
  if (item.sequence != null) return item.sequence;

  const code = (item.xactimate_code || '').toUpperCase();
  const codePrefix = code.split(' ')[0];
  // Texture after replacements, before paint
  if (code.startsWith('DRY TEX')) return 50;
  // Paint last
  if (codePrefix === 'PNT') return 60;
  // Cleaning / treating slot — after removals, before replacements
  if (codePrefix === 'CLN' || codePrefix === 'WTR') {
    // WTR is mostly removals, but extraction/dehu/treating land here too.
    // Defer to activity code when present.
    return ACTIVITY_PRIORITY[item.activity || ''] ?? 2;
  }
  return ACTIVITY_PRIORITY[item.activity || ''] ?? 5;
}

/**
 * Sorts items by section order then by workflow order. Used by the flat-
 * list views that don't render section headings.
 */
function sortByHeadingOrder(items: ScopeResponseLineItem[]): ScopeResponseLineItem[] {
  return [...items].sort((a, b) => {
    const da = categorizeItem(a) - categorizeItem(b);
    if (da !== 0) return da;
    return workflowOrderForItem(a) - workflowOrderForItem(b);
  });
}

/**
 * Group items into ordered sections with workflow-sorted items within.
 * Returns Array<{section, label, items}> ready for rendering with sub-headings.
 */
export function groupItemsBySection(
  items: ScopeResponseLineItem[]
): Array<{ section: string; label: string; items: ScopeResponseLineItem[] }> {
  // When the backend supplied ordering (surface + sequence), order groups by
  // their minimum sequence so the backend's taxonomy AND water/flood reversal
  // are honored. Otherwise fall back to the static SECTION_ORDER (legacy).
  const useBackendOrder = items.some((i) => i.sequence != null && !!i.surface);

  const buckets = new Map<string, ScopeResponseLineItem[]>();
  for (const item of items) {
    const section = sectionForItem(item);
    if (!buckets.has(section)) buckets.set(section, []);
    buckets.get(section)!.push(item);
  }
  const sections: Array<{
    section: string; label: string; items: ScopeResponseLineItem[]; orderKey: number;
  }> = [];
  for (const [section, sectionItems] of buckets.entries()) {
    sectionItems.sort((a, b) => workflowOrderForItem(a) - workflowOrderForItem(b));
    const orderKey = useBackendOrder
      ? Math.min(
          ...sectionItems.map((i) =>
            i.sequence != null ? i.sequence : Number.MAX_SAFE_INTEGER
          )
        )
      : SECTION_ORDER[section] ?? SECTION_ORDER.general;
    sections.push({
      section,
      label: SECTION_LABEL[section] ?? section,
      items: sectionItems,
      orderKey,
    });
  }
  sections.sort((a, b) => a.orderKey - b.orderKey);
  return sections.map(({ section, label, items: groupItems }) => ({
    section,
    label,
    items: groupItems,
  }));
}

// ================================================================= //
//          ScopeListView Component                                    //
// ================================================================= //

interface ScopeListViewProps {
  rooms: ScopeResponseRoom[];
  generalConditions?: ScopeResponseLineItem[];
  showSources?: boolean;
  showDebug?: boolean;
  stepMode?: boolean;
}

/**
 * Continuous list view grouped by room headers.
 * Items within each room are sorted by heading order:
 * Ceiling -> Walls -> Trim -> Doors/Windows -> Floor -> etc.
 */
export const ScopeListView: React.FC<ScopeListViewProps> = ({
  rooms,
  generalConditions = [],
  showSources = false,
  showDebug = false,
  stepMode = false,
}) => {
  if (rooms.length === 0 && generalConditions.length === 0) {
    return (
      <div className="rounded-lg border bg-white p-8 text-center text-muted-foreground">
        No scope items to display.
      </div>
    );
  }

  return (
    <div className="space-y-0 divide-y divide-gray-200 rounded-lg border bg-white">
      {rooms.map((room, roomIdx) => (
        <div key={roomIdx} className="p-4">
          {/* Room header */}
          <div className="flex items-center justify-between mb-3">
            <div>
              <h3 className="text-base font-semibold text-foreground">
                {room.room_name}
              </h3>
              <span className="text-xs text-muted-foreground">{room.room_type}</span>
            </div>
            <span className="text-xs text-muted-foreground">
              {room.items.length} items
            </span>
          </div>

          {/* Line items grouped by section (Ceiling / Walls / ... ) and
              workflow-ordered within each section. */}
          <div className="space-y-3">
            {groupItemsBySection(room.items).map((group) => (
              <div key={group.section}>
                <h4 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mt-1 mb-1.5">
                  {group.label}
                </h4>
                <div className="space-y-1.5">
                  {group.items.map((item, itemIdx) => (
                    <ScopeLineItem
                      key={itemIdx}
                      item={item}
                      showSources={showSources}
                      showDebug={showDebug}
                      stepMode={stepMode}
                      compact
                    />
                  ))}
                </div>
              </div>
            ))}
          </div>

          {/* No-match items — activities the LLM identified but retrieval couldn't map (RULE-001) */}
          {room.no_match_items && room.no_match_items.length > 0 && (
            <div className="mt-3 rounded border-l-4 border-l-red-400 bg-red-50 px-3 py-2">
              <p className="text-xs font-medium text-red-900">
                Activities needing manual code lookup:
              </p>
              <ul className="mt-1 text-xs text-red-800 space-y-1">
                {room.no_match_items.map((nm, i) => (
                  <NoMatchRow
                    key={i}
                    item={nm}
                    showDebug={showDebug}
                    stepMode={stepMode}
                  />
                ))}
              </ul>
            </div>
          )}

          {/* Room notes */}
          {room.notes.length > 0 && (
            <div className="mt-3 rounded bg-slate-50 px-3 py-2">
              <p className="text-xs font-medium text-muted-foreground">Notes:</p>
              <ul className="mt-1 list-disc pl-4 text-xs text-muted-foreground">
                {room.notes.map((note, i) => (
                  <li key={i}>{note}</li>
                ))}
              </ul>
            </div>
          )}
        </div>
      ))}

      {/* General conditions */}
      {generalConditions.length > 0 && (
        <div className="p-4">
          <h3 className="text-base font-semibold text-foreground mb-3">
            General Conditions
          </h3>
          <div className="space-y-3">
            {groupItemsBySection(generalConditions).map((group) => (
              <div key={group.section}>
                <h4 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mt-1 mb-1.5">
                  {group.label}
                </h4>
                <div className="space-y-1.5">
                  {group.items.map((item, idx) => (
                    <ScopeLineItem
                      key={idx}
                      item={item}
                      showSources={showSources}
                      compact
                    />
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};
